import {
  Car,
  Clock,
  LogOut,
  Mail,
  Package,
  Repeat,
  UserPlus,
  Briefcase,
  AlertTriangle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import type { InvitationType } from '@/types';

// ─── Invitation type chip config ─────────────────────────────────────────────

interface ChipConfig {
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  className: string;
}

const INVITATION_TYPE_CHIPS: Record<InvitationType, ChipConfig> = {
  single_visit: {
    labelKey: 'guard.queue.chipInvited',
    icon: Mail,
    className: 'bg-[var(--status-blue-tint)] text-[var(--status-blue-text)]',
  },
  recurring: {
    labelKey: 'guard.queue.chipRecurring',
    icon: Repeat,
    className: 'bg-[rgba(244,162,97,0.10)] text-[var(--text-accent)]',
  },
  domestic_worker: {
    labelKey: 'guard.queue.chipRecurring',
    icon: Repeat,
    className: 'bg-[rgba(244,162,97,0.10)] text-[var(--text-accent)]',
  },
  delivery: {
    labelKey: 'guard.queue.chipDelivery',
    icon: Package,
    className: 'bg-[rgba(201,191,168,0.12)] text-[var(--text-secondary)]',
  },
  taxi: {
    labelKey: 'guard.queue.chipTaxi',
    icon: Car,
    className: 'bg-[rgba(201,191,168,0.12)] text-[var(--text-secondary)]',
  },
  walk_in: {
    labelKey: 'guard.queue.chipWalkIn',
    icon: UserPlus,
    className: 'bg-[var(--status-purple-tint)] text-[var(--status-purple-text)]',
  },
  employee: {
    labelKey: 'guard.queue.chipEmployee',
    icon: Briefcase,
    className: 'bg-[var(--status-blue-tint)] text-[var(--status-blue-text)]',
  },
  event: {
    labelKey: 'guard.queue.chipInvited',
    icon: Mail,
    className: 'bg-[var(--status-blue-tint)] text-[var(--status-blue-text)]',
  },
  trusted_operator: {
    labelKey: 'guard.queue.chipTrustedOperator',
    icon: Mail,
    className: 'bg-[var(--status-purple-tint)] text-[var(--status-purple-text)]',
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTime(isoString: string): string {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatElapsedSince(isoString: string): string {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '—';
  const elapsed = Date.now() - d.getTime();
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

// ─── InvitationTypeChip ──────────────────────────────────────────────────────

function InvitationTypeChip({ type }: { type: InvitationType }) {
  const { t } = useTranslation();
  const config = INVITATION_TYPE_CHIPS[type];
  if (!config) return null;

  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-[var(--radius-md)] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${config.className}`}
    >
      <Icon className="size-3" aria-hidden="true" />
      {t(config.labelKey)}
    </span>
  );
}

// ─── VisitorCard Props ───────────────────────────────────────────────────────

export interface CheckedInCardProps {
  variant: 'checked-in';
  visitId: string;
  visitorName: string;
  hostUnit: string;
  checkedInAt: string;
  invitationType: InvitationType;
  vehiclePlate?: string;
  onCheckOut: (visitId: string) => void;
  isCheckingOut?: boolean;
  /** Detain-on-exit flag state. */
  isFlagged?: boolean;
  flagReason?: string | null;
  /** Safe, generic message the guard can read to the visitor about the delay. */
  visitorDelayMessage?: string;
  onSetAside?: (visitId: string) => void;
  isSettingAside?: boolean;
}

export interface UpcomingCardProps {
  variant: 'upcoming';
  invitationId: string;
  visitorName: string;
  hostUnit: string;
  hostName: string;
  invitationType: InvitationType;
  validFrom: string;
  validUntil: string;
  vehiclePlate?: string;
}

export type VisitorCardProps = CheckedInCardProps | UpcomingCardProps;

/**
 * Reusable visitor row card for the gate queue.
 * Displays visitor name, host unit, timestamps, and invitation type chips.
 */
export function VisitorCard(props: VisitorCardProps) {
  const { t } = useTranslation();

  if (props.variant === 'checked-in') {
    const flagged = props.isFlagged === true;
    return (
      <div
        className={[
          'rounded-[var(--radius-lg)] border p-4 transition-colors',
          flagged
            ? 'border-[var(--border-error)] bg-[var(--status-red-tint)]'
            : 'border-[var(--border-default)] bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-raised)]',
        ].join(' ')}
      >
        <div className="flex items-center gap-4">
          <div className="flex flex-1 flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-[var(--text-primary)] truncate">
                {props.visitorName}
              </span>
              <InvitationTypeChip type={props.invitationType} />
              {flagged && (
                <span className="inline-flex items-center gap-1 rounded-[var(--radius-md)] bg-[var(--status-red-tint)] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-error)]">
                  <AlertTriangle className="size-3" aria-hidden="true" />
                  {t('guard.flag.badge')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)] flex-wrap">
              <span>{props.hostUnit}</span>
              <span className="flex items-center gap-1">
                <Clock className="size-3" aria-hidden="true" />
                {t('guard.queue.checkedInAt')} {formatTime(props.checkedInAt)}
                {' · '}
                {formatElapsedSince(props.checkedInAt)}
              </span>
              {props.vehiclePlate && (
                <span className="flex items-center gap-1">
                  <Car className="size-3" aria-hidden="true" />
                  {props.vehiclePlate}
                </span>
              )}
            </div>
          </div>
          {flagged ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => props.onSetAside?.(props.visitId)}
              disabled={props.isSettingAside}
              className="min-w-[44px] min-h-[44px] shrink-0"
              aria-label={`${t('guard.flag.setAside')} ${props.visitorName}`}
            >
              {t('guard.flag.setAside')}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => props.onCheckOut(props.visitId)}
              disabled={props.isCheckingOut}
              className="min-w-[44px] min-h-[44px] shrink-0"
              aria-label={
                props.visitorName
                  ? `${t('guard.queue.checkOut')} ${props.visitorName}`
                  : t('guard.queue.checkOut')
              }
            >
              <LogOut className="size-4 mr-1" aria-hidden="true" />
              {t('guard.queue.checkOut')}
            </Button>
          )}
        </div>

        {flagged && (
          <div
            role="alert"
            className="mt-3 rounded-[var(--radius-md)] border border-[var(--border-error)] bg-[var(--bg-surface)] p-3"
          >
            <p className="flex items-center gap-1.5 text-sm font-semibold text-[var(--text-error)]">
              <AlertTriangle className="size-4" aria-hidden="true" />
              {t('guard.flag.detainHeading')}
            </p>
            {props.flagReason && (
              <p className="mt-2 text-xs text-[var(--text-secondary)]">
                <span className="font-medium text-[var(--text-primary)]">
                  {t('guard.flag.managementNote')}:{' '}
                </span>
                {props.flagReason}
              </p>
            )}
            <p className="mt-2 text-xs text-[var(--text-secondary)]">
              <span className="font-medium text-[var(--text-primary)]">
                {t('guard.flag.tellVisitor')}:{' '}
              </span>
              {props.visitorDelayMessage ?? t('guard.flag.visitorDelayMessage')}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Upcoming variant
  return (
    <div className="flex items-center gap-4 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 transition-colors hover:bg-[var(--bg-surface-raised)]">
      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-[var(--text-primary)] truncate">
            {props.visitorName}
          </span>
          <InvitationTypeChip type={props.invitationType} />
        </div>
        <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)] flex-wrap">
          <span>{props.hostUnit}</span>
          <span>{props.hostName}</span>
          <span className="flex items-center gap-1">
            <Clock className="size-3" aria-hidden="true" />
            {formatTime(props.validFrom)} – {formatTime(props.validUntil)}
          </span>
          {props.vehiclePlate && (
            <span className="flex items-center gap-1">
              <Car className="size-3" aria-hidden="true" />
              {props.vehiclePlate}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
